import { ErrorInfo } from 'react';

export enum ErrorType {
  NETWORK = 'NETWORK',
  VALIDATION = 'VALIDATION',
  PERMISSION = 'PERMISSION',
  UNKNOWN = 'UNKNOWN',
}

export interface AppError {
  type: ErrorType;
  message: string;
  originalError?: Error;
  metadata?: Record<string, unknown>;
}

export const categorizeError = (error: Error): ErrorType => {
  const message = error.message.toLowerCase();

  if (message.includes('network') || message.includes('fetch')) {
    return ErrorType.NETWORK;
  }

  if (message.includes('validation') || message.includes('invalid')) {
    return ErrorType.VALIDATION;
  }

  if (
    message.includes('permission') ||
    message.includes('unauthorized') ||
    message.includes('forbidden')
  ) {
    return ErrorType.PERMISSION;
  }

  return ErrorType.UNKNOWN;
};

export const getErrorMessage = (error: Error | AppError): string => {
  if ('type' in error) {
    switch (error.type) {
      case ErrorType.NETWORK:
        return 'Unable to connect to the server. Please check your internet connection and try again.';
      case ErrorType.VALIDATION:
        return 'The data provided is invalid. Please check your input and try again.';
      case ErrorType.PERMISSION:
        return 'You do not have permission to perform this action.';
      default:
        return error.message || 'An unexpected error occurred. Please try again.';
    }
  }

  const type = categorizeError(error);
  return getErrorMessage({ type, message: error.message });
};

export const logError = (error: Error, errorInfo?: ErrorInfo, context?: string) => {
  const errorData = {
    message: error.message,
    stack: error.stack,
    errorInfo,
    context,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
  };

  if (process.env.NODE_ENV === 'development') {
    console.group(`🚨 Error in ${context || 'Application'}`);
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Full Context:', errorData);
    console.groupEnd();
  }

  return errorData;
};

export const withRetry = async <T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  delay = 1000,
): Promise<T> => {
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      const errorType = categorizeError(lastError);
      if (errorType === ErrorType.PERMISSION || errorType === ErrorType.VALIDATION) {
        throw lastError;
      }

      if (attempt === maxRetries) {
        throw lastError;
      }

      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }

  throw lastError || new Error('Retry failed with unknown error');
};
